import React, { useState, useEffect } from 'react';
import {
  Key, TrendingUp, DollarSign, Activity,
  AlertCircle, CheckCircle, XCircle, Users, Zap
} from 'lucide-react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { analyticsService, usageService, activityService } from '../services/api';
import toast from 'react-hot-toast';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

function StatCard({ icon: Icon, iconBg, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon size={22} className="text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-0.5 tabular-nums">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-slate-900 text-white rounded-xl px-3 py-2.5 shadow-xl text-xs">
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {formatter ? formatter(p.value, p.name) : p.value}
        </p>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const [trendsData, setTrendsData] = useState([]);
  const [overviewData, setOverviewData] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Use all-time overview (no date range) so stats always show real data
      // Use 365-day trends to cover the full data range
      const [trends, overview, activity] = await Promise.all([
        usageService.getUsageTrends(365),
        usageService.getUsageOverview(undefined, undefined, Date.now()),
        activityService.getRecentActivity(5),
      ]);
      setTrendsData(trends.data.data.trends);
      setOverviewData(overview.data.data);
      setActivityData(activity.data.data.activities || []);
      setLastRefreshed(new Date());
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => {
    if (!n) return '0';
    if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
    return Number(n).toLocaleString();
  };

  const fmtUSD = (v) => {
    const n = parseFloat(v) || 0;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(n);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'api_key_generated': return { icon: CheckCircle, color: 'text-emerald-500' };
      case 'api_key_deleted': return { icon: XCircle, color: 'text-red-500' };
      case 'high_usage': return { icon: Activity, color: 'text-orange-500' };
      case 'usage_limit_warning': return { icon: AlertCircle, color: 'text-amber-500' };
      case 'user_login':
      case 'user_created': return { icon: Users, color: 'text-indigo-500' };
      default: return { icon: Activity, color: 'text-slate-400' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const validModels = (overviewData?.topModels || [])
    .filter(m => parseFloat(m.total_tokens) > 0)
    .map(m => ({ ...m, total_tokens: parseFloat(m.total_tokens) }));

  // Compute total cost from top keys (all-time) as a cross-check
  const totalCost = parseFloat(overviewData?.overview?.total_cost) || 0;
  const totalTokens = parseFloat(overviewData?.overview?.total_tokens) || 0;
  const totalRequests = parseInt(overviewData?.overview?.total_requests) || 0;
  const activeKeys = parseInt(overviewData?.overview?.active_keys) || 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={Key} iconBg="bg-indigo-500" label="Active Keys" value={activeKeys} sub="Currently active" />
        <StatCard icon={Activity} iconBg="bg-emerald-500" label="Total Requests" value={fmt(totalRequests)} sub="All time" />
        <StatCard icon={Zap} iconBg="bg-blue-500" label="Total Tokens" value={fmt(totalTokens)} sub="All time" />
        <StatCard icon={DollarSign} iconBg="bg-violet-500" label="Total Cost" value={fmtUSD(totalCost)} sub="All time" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Usage Trends — Last 365 days</h3>
          {trendsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trendsData}>
                <defs>
                  <linearGradient id="tokensGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date"
                  tickFormatter={v => new Date(v).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                  interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => fmt(v)} />
                <Tooltip content={<CustomTooltip formatter={(v, n) => n === 'total_tokens' ? [fmt(v), 'Tokens'] : [fmtUSD(v), 'Cost']} />} />
                <Area type="monotone" dataKey="total_tokens" stroke="#6366f1" strokeWidth={2} fill="url(#tokensGrad)" />
                <Area type="monotone" dataKey="total_cost" stroke="#10b981" strokeWidth={2} fill="url(#costGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-52 text-slate-400">
              <div className="text-center">
                <Activity size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No usage data yet</p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Model Distribution</h3>
          {validModels.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={220}>
                <PieChart>
                  <Pie data={validModels} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                    dataKey="total_tokens" paddingAngle={3}>
                    {validModels.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => [fmt(v), 'Tokens']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {validModels.slice(0, 5).map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-xs text-slate-600 truncate flex-1">
                      {m.model_name.split('.').pop().replace(/-v\d.*/, '')}
                    </span>
                    <span className="text-xs font-medium text-slate-900 tabular-nums">{fmt(m.total_tokens)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-52 text-slate-400">
              <div className="text-center">
                <Activity size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No usage data yet</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Keys by Cost */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Top Keys by Cost</h3>
          {(overviewData?.topKeys || []).length > 0 ? (
            <div className="space-y-3">
              {(overviewData.topKeys || []).slice(0, 5).map((k, i) => {
                const pct = totalCost > 0 ? (parseFloat(k.total_cost) / totalCost) * 100 : 0;
                return (
                  <div key={k.key_id || i}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-sm text-slate-700 truncate font-medium">{k.key_name}</span>
                      </div>
                      <span className="text-xs font-semibold text-slate-800 tabular-nums ml-3 flex-shrink-0">{fmtUSD(k.total_cost)}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all"
                        style={{ width: pct + '%', background: `linear-gradient(90deg, ${COLORS[i % COLORS.length]}, ${COLORS[i % COLORS.length]}99)` }} />
                    </div>
                  </div>
                );
              })}
              <div className="border-t border-slate-100 pt-3 mt-1 flex justify-between">
                <span className="text-xs text-slate-500 font-medium">Total</span>
                <span className="text-sm font-bold text-slate-900 tabular-nums">{fmtUSD(totalCost)}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-4 text-center">No usage data yet</p>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">System Status</h3>
          <div className="space-y-3">
            {[
              { label: 'API Service', status: 'Operational' },
              { label: 'Database', status: 'Connected' },
              { label: 'AWS Bedrock', status: 'Available' },
            ].map(s => (
              <div key={s.label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <span className="text-sm text-slate-600">{s.label}</span>
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="text-xs font-medium text-emerald-600">{s.status}</span>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between pt-1">
              <span className="text-sm text-slate-600">Data Range</span>
              <span className="text-xs text-slate-400">
                {trendsData.length > 0
                  ? `${new Date(trendsData[0].date).toLocaleDateString('en', { month: 'short', year: 'numeric' })} – ${new Date(trendsData[trendsData.length - 1].date).toLocaleDateString('en', { month: 'short', year: 'numeric' })}`
                  : 'No data'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Last Refreshed</span>
              <span className="text-xs text-slate-400">
                {lastRefreshed ? lastRefreshed.toLocaleTimeString() : 'Just now'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
